import {useEffect, useMemo, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {useCustomQuery, useCustomerId} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBrand, toBrandCode} from '../contexts/brand-context'
import {useEnrichVickyProductsWithStatus} from './use-enrich-vicky-products'

/*
 * SCAPI getProducts accepts at most 24 ids per call and useEnrichVickyProducts does not
 * split its requests, so a page is enriched in two halves. The backend serves at most
 * 2 * ENRICH_BATCH_SIZE products per page.
 */
const ENRICH_BATCH_SIZE = 24

/**
 * @typedef {Object} FypRecosPLPPage
 * @property {Object[]} hits - Products mapped to the PLP search-hit shape.
 * @property {number} total - Backend count of resolved, online, orderable products.
 * @property {number} offset - Offset of this response, which may belong to a retained page.
 */

/**
 * Fetches one page of the Vicky "For You" (fyp-recos) ranking for a category PLP and
 * returns it in the shape of a PLP product search result, so the PLP can paginate it
 * like a regular category search.
 *
 * The backend resolves Vicky's SKUs to orderable products in Vicky's order and reports
 * the total number of orderable ranked products. Personalization parameters are built
 * on the server. The query options include a customer-specific key, but the installed
 * SDK constructs its own effective key from endpoint parameters; these options alone
 * do not guarantee customer isolation.
 *
 * @param {Object} options
 * @param {string} options.categoryId - SFCC category id (Vicky's `category_id`).
 * @param {number} options.offset - Offset of the first product of the page.
 * @param {number} options.limit - Page size (at most 48).
 * @param {boolean} [options.enabled=true] - Request gate; the PLP caller disables it during SSR.
 * @returns {{page: FypRecosPLPPage|null, isFailed: boolean}} A page after enrichment
 *   requests settle, or the previous page of the same category while loading. Consumers
 *   must match its offset before advancing pagination. `isFailed` covers custom API
 *   errors/unusable responses, not enrichment errors. An empty valid page is handled
 *   by the caller's legacy fallback.
 */
export const useFypRecosPLP = ({categoryId, offset, limit, enabled = true}) => {
    const location = useLocation()
    const {site, locale} = useMultiSite()
    const siteId = site?.id
    const {brandId} = useBrand()
    const customerId = useCustomerId()
    const localeId = typeof locale === 'string' ? locale : locale?.id
    const country = localeId?.split(/[-_]/).pop()?.toUpperCase() || 'CA'
    // Vicky expects 'UK' where the locale says 'GB'.
    const countryCode = country === 'GB' ? 'UK' : country
    const isEnabled = enabled && Boolean(categoryId && siteId)

    const {
        data,
        isLoading: isQueryLoading,
        isError
    } = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: {
                    siteId,
                    c_brand: toBrandCode(brandId),
                    c_country: countryCode,
                    c_categoryId: categoryId,
                    c_offset: offset,
                    c_limit: limit,
                    ...(localeId ? {locale: localeId} : {})
                },
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: '/fyp-recos',
                    apiName: 'vickyai'
                }
            },
            rawResponse: false
        },
        {
            queryKey: ['vickyai', '/fyp-recos', categoryId, siteId, customerId, offset, limit],
            enabled: isEnabled,
            staleTime: 5 * 60 * 1000,
            // Fail fast: a failure falls back to the standard PLP, so don't keep shoppers waiting.
            retry: false,
            onError: (err) => {
                console.warn('VickyAI fyp-recos fetch failed:', err)
            }
        }
    )

    // The backend answers `{data, total}`; anything else (an empty array when the Vicky
    // call failed, or a "not enabled" message) is not a usable ranking.
    const isUsable = Array.isArray(data?.data) && Number.isFinite(data?.total)
    const items = useMemo(() => (isUsable ? data.data : []), [isUsable, data])
    const firstHalf = useMemo(() => items.slice(0, ENRICH_BATCH_SIZE), [items])
    const secondHalf = useMemo(() => items.slice(ENRICH_BATCH_SIZE), [items])

    // Availability is already guaranteed by the backend (orderable products only).
    const enrichOptions = {enabled: isEnabled && isUsable, availabilityEnabled: false}
    const {products: enrichedFirst, isFetching: isFirstFetching} = useEnrichVickyProductsWithStatus(
        firstHalf,
        enrichOptions
    )
    const {products: enrichedSecond, isFetching: isSecondFetching} =
        useEnrichVickyProductsWithStatus(secondHalf, enrichOptions)

    // Wait for both enrichment batches to settle before publishing. The enrichment hook
    // does not expose errors here, so completion does not guarantee every detail resolved.
    const readyPage = useMemo(() => {
        if (!isUsable || isFirstFetching || isSecondFetching) return null
        return {
            // Map the enriched items to the search-hit fields the grid reads.
            hits: [...enrichedFirst, ...enrichedSecond]
                .filter((product) => product?.id)
                .map((product) => ({
                    ...product,
                    productId: product.id,
                    productName: product.name,
                    hitType: 'product'
                })),
            total: data.total,
            offset: Number(data.offset ?? offset)
        }
    }, [isUsable, isFirstFetching, isSecondFetching, enrichedFirst, enrichedSecond, data, offset])

    // Keep the previous page of the same category while the next one loads, so the grid
    // does not fall back to skeletons on "show more".
    const lastPageRef = useRef(null)
    if (readyPage) lastPageRef.current = {categoryId, page: readyPage}
    const page =
        readyPage ||
        (lastPageRef.current?.categoryId === categoryId ? lastPageRef.current?.page : null) ||
        null

    // A display indicator only. Native history avoids router navigation, PLP resets
    // and refetches. Reapply after pagination/navigation, preserving history state.
    const hasRenderedProducts = isEnabled && Boolean(page?.hits.length)
    useEffect(() => {
        const url = new URL(window.location.href)
        if (hasRenderedProducts) {
            if (url.searchParams.get('isPLPPersonalized') === 'true') return
            url.searchParams.set('isPLPPersonalized', 'true')
        } else {
            if (!url.searchParams.has('isPLPPersonalized')) return
            url.searchParams.delete('isPLPPersonalized')
        }
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
    }, [hasRenderedProducts, location])

    return {
        page,
        isFailed: isError || (isEnabled && !isQueryLoading && !isUsable)
    }
}
